import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import { uploadFileToS3 } from "../../../../api/s3";
import { fetchAPI } from "../../../../api/utils";
import { IMAGE_MAX_SIZE_300_KB } from "../../../../utils/image";
import styles from "./SocialShare.module.css";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface SocialShareProps {
    navigateToNext: () => void;
}

const SocialShare: React.FC<SocialShareProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const linkId = trackData?.trackId || trackData?.id;

    const [socialShareImage, setSocialShareImage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);
    const [imageError, setImageError] = useState<string | null>(null);

    useEffect(() => {
        // Load social share image from trackData if available
        if (trackData.socialShareImage) {
            setSocialShareImage(trackData.socialShareImage);
        } else if (trackData.socialShareImage === null || trackData.socialShareImage === "") {
            // Explicitly clear if it's null or empty
            setSocialShareImage("");
        }
    }, [trackData.socialShareImage]);

    const handleImageUpload = async (file: File) => {
        if (!file) return;

        if (file.size > IMAGE_MAX_SIZE_300_KB) {
            setImageError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
            return;
        }

        setImageError(null);
        setUploadingImage(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setSocialShareImage(response.url);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
        }
    };

    // Dropzone for social share image
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleImageUpload(acceptedFiles[0]);
            }
        },
        onDropRejected: (fileRejections) => {
            const firstError = fileRejections[0]?.errors[0];
            if (firstError?.code === "file-too-large") {
                setImageError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
            } else if (firstError?.code === "file-invalid-type") {
                setImageError(t("unsupportedFileType"));
            } else {
                setImageError(t("fileUploadFail"));
            }
        },
        accept: { "image/*": [".svg", ".png", ".jpg", ".jpeg", ".webp"] },
        maxSize: IMAGE_MAX_SIZE_300_KB,
        disabled: uploadingImage,
        noClick: true,
    });

    const handleSave = async () => {
        if (!trackData.trackId && !trackData.id) {
            console.error("No trackId or id found in trackData:", trackData);
            return;
        }

        setLoading(true);
        try {
            const trackId = trackData.trackId || trackData.id;

            await fetchAPI(
                `/tracks/${trackId}/social-share`,
                "POST",
                {
                    hasSocialSharePopup: true,
                    socialShareImage: socialShareImage || null,
                },
                token,
            );

            // Update local state immediately
            updateTrackData("hasSocialSharePopup", true);
            updateTrackData("socialShareImage", socialShareImage);
            navigateToNext();
        } catch (error: any) {
            console.error("Error saving social share settings:", error);
            alert(`Error saving social share settings: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                <Link
                    to={`/create/tour/${linkId}/rewards`}
                    style={{ color: "var(--green-600)", textDecoration: "none" }}
                >
                    {t("progression")}
                </Link>
                <span style={{ margin: "0 8px", color: "#999" }}>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("socialMediaShare")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("socialMediaShare")}</h2>
                        <p className={pageHeader.subtitle}>{t("socialMediaShareDescription")}</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className={styles.sectionCard}>
                {/* Illustration to share */}
                <h3 className={styles.sectionTitle}>{t("shareIllustration")}</h3>
                <p className={styles.sectionDescription}>{t("shareIllustrationDescription")}</p>

                <div className={styles.specsContainer}>
                    <span className={styles.specBadge}>{t("avatarRatio")}: 3:4</span>
                    <span className={styles.specBadge}>
                        {t("avatarWeight")}: {t("imageMaxSize300kb")} max.
                    </span>
                </div>

                <div
                    {...getRootProps()}
                    className={`${styles.uploadArea} ${socialShareImage ? styles.hasImage : ""} ${isDragActive ? styles.dragActive : ""}`}
                >
                    <input {...getInputProps()} />
                    {socialShareImage ? (
                        <>
                            <img src={socialShareImage} alt={t("shareIllustration")} className={styles.previewImage} />
                            <p className={styles.uploadAreaText}>{t("dragDropImageHere")}</p>
                        </>
                    ) : (
                        <p className={styles.uploadAreaText}>{t("dragDropImageHere")}</p>
                    )}
                </div>
                {imageError && <p style={{ color: "red", marginTop: "10px" }}>{imageError}</p>}
                <div className={styles.chooseFileButton}>
                    <Button
                        label={t("chooseFile")}
                        onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.accept = "image/*";
                            input.onchange = (e: any) => {
                                if (e.target.files?.[0]) {
                                    handleImageUpload(e.target.files[0]);
                                }
                            };
                            input.click();
                        }}
                        disabled={uploadingImage}
                        className="btn-outline"
                    />
                </div>
            </div>

            {/* Footer buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("cancel")}
                    onClick={() => navigate(`/create/tour/${linkId}/rewards`)}
                    variant="cancel"
                    shape="pill"
                    className={stepStyles.footerBtn}
                />
                <Button
                    label={loading ? t("saving") : t("continue")}
                    onClick={handleSave}
                    disabled={loading || uploadingImage}
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${stepStyles.footerBtn}`}
                />
            </div>
        </div>
    );
};

export default SocialShare;
